import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Skill {
    _id: string;
    name: string;
    description?: string;
    usageCount: number;
}

interface MultiSkillSelectProps {
    selectedSkills: string[];
    onSkillsChange: (skillIds: string[]) => void;
    placeholder?: string;
    showSearch?: boolean;
    className?: string;
    disabled?: boolean;
    maxSkills?: number;
}

const MultiSkillSelect: React.FC<MultiSkillSelectProps> = ({
    selectedSkills,
    onSkillsChange,
    placeholder = "Select skills...",
    showSearch = true,
    className = "",
    disabled = false,
    maxSkills = 10
}) => {
    const dispatch = useAppDispatch();
    const [skills, setSkills] = useState<Skill[]>([]);
    const [filteredSkills, setFilteredSkills] = useState<Skill[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const fetchSkills = useCallback(async () => {
        try {
            setLoading(true);
            const endpoint = searchTerm && searchTerm.length >= 2
                ? `/skills/search?q=${searchTerm}`
                : '/skills/all';
            const response = await api.get(endpoint);
            setSkills(response.data.data);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch skills',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, searchTerm]);

    useEffect(() => {
        fetchSkills();
    }, [fetchSkills]);

    useEffect(() => {
        let filtered = skills;

        if (searchTerm) {
            const searchRegex = new RegExp(searchTerm, 'i');
            filtered = filtered.filter(skill =>
                skill.name.match(searchRegex) ||
                (skill.description && skill.description.match(searchRegex))
            );
        }

        setFilteredSkills(filtered);
    }, [skills, searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSkillToggle = (skillId: string) => {
        if (selectedSkills.includes(skillId)) {
            onSkillsChange(selectedSkills.filter(id => id !== skillId));
        } else {
            if (selectedSkills.length < maxSkills) {
                onSkillsChange([...selectedSkills, skillId]);
            } else {
                dispatch(showToast({
                    message: `You can select maximum ${maxSkills} skills`,
                    type: 'warning'
                }));
            }
        }
    };

    const removeSkill = (skillId: string) => {
        onSkillsChange(selectedSkills.filter(id => id !== skillId));
    };

    const getSelectedSkills = () => {
        return skills.filter(skill => selectedSkills.includes(skill._id));
    };

    const getUnselectedSkills = () => {
        return filteredSkills.filter(skill => !selectedSkills.includes(skill._id));
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Selected Skills Display */}
            <div
                className={`min-h-[42px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-3 flex items-center justify-between cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="flex-1 flex flex-wrap gap-1">
                    {selectedSkills.length === 0 ? (
                        <span className="text-sm text-gray-500 dark:text-gray-400">{placeholder}</span>
                    ) : (
                        getSelectedSkills().map((skill) => (
                            <span
                                key={skill._id}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                            >
                                {skill.name}
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSkill(skill._id);
                                        }}
                                        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                                    >
                                        <XMarkIcon className="w-3 h-3" />
                                    </button>
                                )}
                            </span>
                        ))
                    )}
                </div>
                {!disabled && (
                    <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </div>

            {/* Skills Count */}
            {selectedSkills.length > 0 && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} selected
                    {maxSkills && ` (max ${maxSkills})`}
                </div>
            )}

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-hidden">
                    {/* Search */}
                    {showSearch && (
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="relative">
                                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search skills..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    {/* Skills List */}
                    <div className="max-h-48 overflow-y-auto">
                        {loading ? (
                            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                                Loading skills...
                            </div>
                        ) : getUnselectedSkills().length === 0 ? (
                            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                                {selectedSkills.length >= maxSkills ? 'Maximum skills selected' : 'No skills found'}
                            </div>
                        ) : (
                            getUnselectedSkills().map((skill) => (
                                <button
                                    key={skill._id}
                                    type="button"
                                    onClick={() => handleSkillToggle(skill._id)}
                                    disabled={selectedSkills.length >= maxSkills}
                                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {skill.name}
                                            </div>
                                            {skill.description && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {skill.description}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-xs text-gray-400">
                                                {skill.usageCount}
                                            </span>
                                            <div className={`w-4 h-4 border-2 rounded ${selectedSkills.includes(skill._id)
                                                    ? 'bg-blue-500 border-blue-500'
                                                    : 'border-gray-300 dark:border-gray-600'
                                                }`}>
                                                {selectedSkills.includes(skill._id) && (
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSkillSelect;
